import React, { useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";

import { useAuth } from "../context/authContext";
import isInAppBrowser from "../hooks/useInAppBrowserWarning";
import InAppBrowserDialog from "./InAppBrowserDialog";

/**
 * Shared "Continue with Google" button used on the landing hero and in the
 * auth-prompt dialog. Centralises two behaviours both call sites need:
 *   - guard against in-app browsers (where Google OAuth is unreliable) by
 *     surfacing a warning dialog instead of starting login
 *   - a disabled loading state while the login request is in flight
 *
 * The label is kept in the layout (just hidden) while loading and the spinner
 * is overlaid on top, so the button's resting width never shifts.
 *
 * `disabled` and any extra Button props (e.g. sx, fullWidth) are passed through.
 */
export default function GoogleLoginButton({ disabled, sx, ...buttonProps }) {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [browserDialogOpen, setBrowserDialogOpen] = useState(false);

  const handleClick = async () => {
    if (isInAppBrowser()) {
      setBrowserDialogOpen(true);
      return;
    }
    setLoading(true);
    // Keep the button disabled right up to navigation: login() resolves to true
    // once the redirect has been kicked off, so we only re-enable on failure
    // (where it returns false / throws) rather than in the success window.
    try {
      const redirecting = await login();
      if (!redirecting) setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleClick}
        disabled={disabled || loading}
        sx={{ position: "relative", ...sx }}
        {...buttonProps}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            whiteSpace: "nowrap",
            visibility: loading ? "hidden" : "visible",
          }}
        >
          <GoogleIcon fontSize="small" />
          Continue with Google
        </Box>
        {loading && (
          <Box
            component="span"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <CircularProgress size={18} color="inherit" />
            Loading
          </Box>
        )}
      </Button>

      <InAppBrowserDialog
        open={browserDialogOpen}
        onClose={() => setBrowserDialogOpen(false)}
      />
    </>
  );
}
